import { useEffect, useState, useRef } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth, sendEmailVerificationForUser } from '../firebase';
import { getStreaks } from '../utils/streakUtils';
import UserCard from '../components/UserCard';
import UserCardShareable from '../components/UserCardShareable';
import { badgeConfig } from '../config/badgeConfig';
import { reauthenticateWithCredential, EmailAuthProvider, updatePassword } from 'firebase/auth';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { containsBadWord } from '../utils/badWords';
import { toast } from "sonner";
import { examTypes } from '../utils/config';
import { levelConfig, getLevelInfo } from '../config/levelConfig';
import '../styles/AccountPage.css';

import GlassCard from '../components/ui/GlassCard';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Avatar from '../components/Avatar';

// Removed old AVATARS array

const CHARACTER_TYPES = [
  { id: 'kitap-kurdu', name: 'Kitap Kurdu', emoji: '📚', desc: 'Bilgiyi sünger gibi emer, detayları kaçırmaz.' },
  { id: 'gece-savascisi', name: 'Gece Savaşçısı', emoji: '🦉', desc: 'Herkes uyurken o çalışır, karanlıkta parlar.' },
  { id: 'parlayan-yildiz', name: 'Parlayan Yıldız', emoji: '⭐', desc: 'Denemelerde hep zirvede, ilham verir.' },
  { id: 'merakli-gezgin', name: 'Meraklı Gezgin', emoji: '🧭', desc: 'Farklı kaynakları tarar, yeni yollar keşfeder.' },
  { id: 'disiplin-ustasi', name: 'Disiplin Ustası', emoji: '⚔️', desc: 'Programına sadık, asla pes etmez.' },
  { id: 'zaman-bukucu', name: 'Zaman Bükücü', emoji: '⏳', desc: 'Pomodoro tekniklerinin efendisi, zamanla yarışır.' },
  { id: 'sessiz-golge', name: 'Sessiz Gölge', emoji: '🥷', desc: 'Kimseye duyurmadan arka planda en çok o çalışır.' },
  { id: 'bilim-insani', name: 'Çılgın Bilim İnsanı', emoji: '🧪', desc: 'Her soruya analitik yaklaşır, formüllere aşıktır.' },
  { id: 'motivasyon-kocu', name: 'Motivasyon Kaynağı', emoji: '🔥', desc: 'Etrafındakileri alevlendirir, takımı yukarı çeker.' }
];

function AccountPage() {
  const userId = localStorage.getItem('userId');
  const navigate = useNavigate();

  const [userData, setUserData] = useState({
    username: '', emailVerified: false, referralCode: '', referralCodeUsed: null,
    examType: 'TYT–AYT', about: '', completedGoals: 0, badges: [], streakBadges: [],
    target: '', avatar: '', characterType: '', xp: 0, level: 1, socialPoints: 0, currentStreak: 0,
  });
  const [initialData, setInitialData] = useState(null);
  const [newExamType, setNewExamType] = useState('');

  // Modals
  const [showCharacterModal, setShowCharacterModal] = useState(false);
  const [showShareableCard, setShowShareableCard] = useState(false);
  const [showBadgesModal, setShowBadgesModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showLevelUpModal, setShowLevelUpModal] = useState(false);

  // Form
  const [newPassword, setNewPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newReferralCodeUsed, setNewReferralCodeUsed] = useState('');
  const [newLevel, setNewLevel] = useState(null);
  const [prevLevel, setPrevLevel] = useState(null);

  const totalBadges = Object.keys(badgeConfig).length;
  const earnedBadges = [...userData.badges, ...userData.streakBadges].length;
  const badgePercent = Math.round((earnedBadges / totalBadges) * 100);
  const xpProgress = (userData.xp % 100) || 0;
  const currentLevel = getLevelInfo(userData.level);

  // ── Fetch ──
  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', userId));
        if (snap.exists()) {
          const d = snap.data();
          const streaks = await getStreaks(userId);
          setUserData({
            username: d.username || '', emailVerified: d.emailVerified || false,
            referralCode: d.referralCode || '', referralCodeUsed: d.referralCodeUsed || null,
            examType: d.examType || 'TYT–AYT', about: d.about || '', completedGoals: d.completedGoals || 0,
            badges: d.badges || [], streakBadges: d.streakBadges || [], target: d.target || '',
            avatar: d.avatar || '', characterType: d.characterType || '',
            xp: d.xp || 0, level: d.level || 1, socialPoints: d.socialPoints || 0,
            currentStreak: streaks.currentStreak || 0,
          });
          setInitialData({
            username: d.username || '', 
            examType: d.examType || 'TYT–AYT', 
            about: d.about || '', 
            target: d.target || '',
            avatar: d.avatar || '', 
            characterType: d.characterType || '',
          });
          setNewExamType(d.examType || 'TYT–AYT');
          setPrevLevel(d.level || 1);
        }
      } catch (err) { toast.error(`Veri çekilemedi: ${err.message}`); }
    })();
  }, [userId]);

  // Level up detection
  useEffect(() => {
    if (prevLevel !== null && userData.level > prevLevel) {
      setNewLevel(userData.level);
      setShowLevelUpModal(true);
      setTimeout(() => setShowLevelUpModal(false), 5000);
      setPrevLevel(userData.level);
    }
  }, [userData.level, prevLevel]);

  // ── Handlers ──
  const handleVerifyEmail = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return toast.error('Oturum bulunamadı.');
      if (user.emailVerified) return toast.info('E-posta zaten doğrulanmış.');
      await sendEmailVerificationForUser(user);
      toast.success('Doğrulama e-postası gönderildi!');
      let attempts = 0;
      const check = setInterval(async () => {
        attempts++;
        await user.reload();
        if (user.emailVerified) {
          clearInterval(check);
          await updateDoc(doc(db, 'users', userId), { emailVerified: true, xp: (userData.xp || 0) + 50 });
          setUserData((p) => ({ ...p, emailVerified: true, xp: p.xp + 50 }));
          toast.success('E-posta doğrulandı! 50 XP kazandın!');
        } else if (attempts >= 60) { clearInterval(check); toast.info('Doğrulama süresi doldu.'); }
      }, 3000);
    } catch (err) { toast.error(`Doğrulama hatası: ${err.message}`); }
  };

  const handleUpdatePassword = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return toast.error('Oturum bulunamadı.');
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, currentPassword));
      await updatePassword(user, newPassword);
      toast.success('Şifre güncellendi!');
      setNewPassword(''); setCurrentPassword(''); setShowPasswordModal(false);
    } catch (err) { toast.error(`Şifre güncellenemedi: ${err.message}`); }
  };

  const handleUpdateReferral = async () => {
    try {
      if (userData.referralCodeUsed) return toast.error('Referans kodu zaten eklenmiş.');
      if (containsBadWord(newReferralCodeUsed)) return toast.error('Uygunsuz kelime içeriyor.');
      await updateDoc(doc(db, 'users', userId), { referralCodeUsed: newReferralCodeUsed });
      setUserData((p) => ({ ...p, referralCodeUsed: newReferralCodeUsed }));
      toast.success('Referans kodu eklendi!');
      setNewReferralCodeUsed(''); setShowReferralModal(false);
    } catch (err) { toast.error(`Hata: ${err.message}`); }
  };

  const handleSave = async () => {
    try {
      if (containsBadWord(userData.about)) return toast.error('Hakkımda kısmı uygun olmayan kelimeler içeriyor.');
      const ref = doc(db, 'users', userId);
      if (newExamType && newExamType !== userData.examType) {
        await updateDoc(ref, { examType: newExamType });
        localStorage.setItem('examType', newExamType);
      }
      await updateDoc(ref, {
        about: userData.about, target: userData.target,
        avatar: userData.avatar, characterType: userData.characterType,
      });
      setInitialData({
        ...initialData,
        examType: newExamType,
        about: userData.about,
        target: userData.target,
        avatar: userData.avatar,
        characterType: userData.characterType,
      });
      toast.success('Profil güncellendi!');
    } catch (err) { toast.error(`Kayıt hatası: ${err.message}`); }
  };

  const handleDiceBearRoll = () => {
    const randomSeed = Math.random().toString(36).substring(7);
    const currentStyle = getDiceBearStyle(userData.avatar) || 'micah';
    setUserData({
      ...userData,
      avatar: JSON.stringify({ type: 'dicebear', style: currentStyle, seed: randomSeed })
    });
  };

  const handleDiceBearChange = (key, value) => {
    let currentData = { type: 'dicebear', style: 'micah', seed: userData.username };
    if (userData.avatar && userData.avatar.startsWith('{')) {
      try {
        currentData = JSON.parse(userData.avatar);
      } catch(e) {}
    }
    currentData[key] = value;
    setUserData({
      ...userData,
      avatar: JSON.stringify(currentData)
    });
  };

  const getDiceBearStyle = (avatarStr) => {
    if (avatarStr && avatarStr.startsWith('{')) {
      try {
        const parsed = JSON.parse(avatarStr);
        return parsed.style;
      } catch(e) {}
    }
    return null;
  };

  const isDirty = initialData && (
    newExamType !== initialData.examType ||
    userData.about !== initialData.about ||
    userData.target !== initialData.target ||
    userData.avatar !== initialData.avatar ||
    userData.characterType !== initialData.characterType
  );

  return (
    <motion.div className="account-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <h1 className="account-page-title">
        <span className="text-gradient">Hesabım</span>
      </h1>

      {/* ── Hero Banner ── */}
      <div className="account-hero-banner">
        <div className="hero-avatar-wrapper">
          <Avatar avatarData={userData.avatar} seedFallback={userData.username} size={90} />
        </div>
        <div className="hero-info">
          <h2 className="hero-username">{userData.username}</h2>
          <span className="hero-character">{userData.characterType || 'Karakter seçilmedi'}</span>
          
          <div className="hero-level-row">
            <span className="hero-level-text">
              {currentLevel.emoji} {currentLevel.name} (Sv. {userData.level})
            </span>
            <div className="hero-xp-bar-container">
              <div className="hero-xp-bar">
                <motion.div className="hero-xp-fill" initial={{ width: 0 }} animate={{ width: `${xpProgress}%` }} transition={{ duration: 1 }} />
              </div>
              <span className="hero-xp-text">{userData.xp % 100}/100 XP</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bento-account-dashboard">
        
        {/* Stats & Badges Card */}
        <GlassCard className="bento-col-span-8">
          <div className="bento-card-header">
            <h3 className="bento-card-title">📊 İstatistikler & Rozetler</h3>
          </div>
          
          <div className="stats-mini-grid">
            <div className="stat-mini">
              <span className="stat-mini-value">{userData.currentStreak}</span>
              <span className="stat-mini-label">🔥 Streak</span>
            </div>
            <div className="stat-mini">
              <span className="stat-mini-value">{userData.socialPoints}</span>
              <span className="stat-mini-label">👏 Sosyal Puan</span>
            </div>
            <div className="stat-mini">
              <span className="stat-mini-value">{userData.completedGoals}</span>
              <span className="stat-mini-label">🎯 Hedefler</span>
            </div>
          </div>

          {/* Cyberpunk Style Badges & XP Overview */}
          <div className="xp-badges-overview mt-6" style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '12px', padding: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#00ffcc', textTransform: 'uppercase', letterSpacing: '1px' }}>Kozmik Koleksiyon</span>
              <Button variant="ghost" size="sm" onClick={() => setShowBadgesModal(true)} style={{ fontSize: '12px', padding: '4px 8px' }}>Tümünü Gör ({earnedBadges}/{totalBadges})</Button>
            </div>
            
            <div className="badges-grid" style={{ gap: '10px', gridTemplateColumns: 'repeat(auto-fill, minmax(40px, 1fr))' }}>
              {[...userData.badges, ...userData.streakBadges].slice(0, 7).map((b, i) => (
                <motion.div key={i} className="badge-item" title={badgeConfig[b]?.name || b} whileHover={{ scale: 1.1, rotate: 5 }} style={{ width: '40px', height: '40px', fontSize: '20px', background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.01))', border: '1px solid rgba(0, 255, 204, 0.2)', boxShadow: '0 0 10px rgba(0, 255, 204, 0.1)' }}>
                  {badgeConfig[b]?.icon || '🏅'}
                </motion.div>
              ))}
              {earnedBadges > 7 && (
                 <div className="badge-item" onClick={() => setShowBadgesModal(true)} style={{ width: '40px', height: '40px', fontSize: '12px', background: 'rgba(0,0,0,0.3)', border: '1px dashed rgba(255,255,255,0.2)', cursor: 'pointer', color: '#94a3b8' }}>
                   +{earnedBadges - 7}
                 </div>
              )}
            </div>
            
            <div className="badge-progress-bar mt-4" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
               <div className="hero-xp-bar" style={{height: '8px', flex: 1, background: 'rgba(0,0,0,0.5)', borderRadius: '4px'}}>
                 <motion.div className="hero-xp-fill" initial={{width: 0}} animate={{ width: `${badgePercent}%` }} style={{ background: 'linear-gradient(90deg, #00c853, #00ffcc)', boxShadow: '0 0 10px rgba(0, 255, 204, 0.5)', borderRadius: '4px' }} />
               </div>
               <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 'bold' }}>{badgePercent}%</span>
            </div>
          </div>
        </GlassCard>

        {/* Avatar Studio */}
        <GlassCard className="bento-col-span-4 avatar-studio-container">
           <div className="bento-card-header w-full text-center" style={{justifyContent: 'center', marginBottom: 0}}>
             <h3 className="bento-card-title text-center" style={{justifyContent: 'center'}}>🎨 Avatar Stüdyosu</h3>
           </div>
           
           <div className="avatar-preview-box">
              <Avatar avatarData={userData.avatar} seedFallback={userData.username} size={80} />
           </div>
           
           <div className="avatar-controls mt-2">
              <select 
                className="edit-input mb-2 text-center" 
                onChange={(e) => handleDiceBearChange('style', e.target.value)}
                value={getDiceBearStyle(userData.avatar) || 'micah'}
              >
                <option value="micah">Micah (Modern)</option>
                <option value="avataaars">Avataaars (Karakter)</option>
                <option value="bottts">Bottts (Robot)</option>
                <option value="funEmoji">Fun Emoji (Eğlenceli)</option>
                <option value="lorelei">Lorelei (Tatlı)</option>
                <option value="notionists">Notionists (Çizim)</option>
              </select>
              <Button variant="secondary" onClick={handleDiceBearRoll} style={{width: '100%'}}>
                🎲 Rastgele Üret
              </Button>
           </div>
        </GlassCard>

        {/* Personal Info Card */}
        <GlassCard className="bento-col-span-6">
          <div className="bento-card-header">
            <h3 className="bento-card-title">✍️ Kişisel Bilgiler</h3>
          </div>
          
          <div className="edit-field">
            <label className="edit-label">Hedefin</label>
            <input className="edit-input" value={userData.target} onChange={(e) => setUserData({ ...userData, target: e.target.value })} placeholder="Hedefini (örn: ODTÜ Bilgisayar) yaz..." />
          </div>

          <div className="edit-field">
            <label className="edit-label">Hakkımda</label>
            <textarea className="edit-textarea" value={userData.about} onChange={(e) => setUserData({ ...userData, about: e.target.value })} placeholder="Kendinden biraz bahset..." />
          </div>

          <div className="flex gap-3 flex-wrap mt-2">
            <Button variant="primary" size="sm" onClick={() => setShowShareableCard(true)}>Kartını Paylaş</Button>
            <Button variant="secondary" size="sm" onClick={() => navigate('/streaks')}>Streak Tablosuna Git</Button>
          </div>
        </GlassCard>

        {/* Exam & Character Settings */}
        <GlassCard className="bento-col-span-6">
          <div className="bento-card-header">
            <h3 className="bento-card-title">⚙️ Sistem Tercihleri</h3>
          </div>

          <div className="edit-field">
            <label className="edit-label">Sınav Türü</label>
            <select className="edit-input" value={newExamType} onChange={(e) => setNewExamType(e.target.value)}>
              {examTypes.map((exam) => (
                <option key={exam.id || exam} value={exam.name || exam}>{exam.name || exam}</option>
              ))}
            </select>
          </div>

          <div className="edit-field mt-4">
            <label className="edit-label">Karakter Tipi</label>
            <Button variant="ghost" onClick={() => setShowCharacterModal(true)} style={{width: '100%', justifyContent: 'space-between'}}>
              <span>{userData.characterType || 'Seçim Yapılmadı'}</span>
              <span>✏️ Değiştir</span>
            </Button>
          </div>
          
          <div className="bento-card-header mt-6">
            <h3 className="bento-card-title">🔐 Güvenlik ve Gizlilik</h3>
          </div>
          
          <div className="settings-item">
            <span className="settings-label">✉️ {auth.currentUser?.email || 'E-posta bulunamadı'}</span>
            {auth.currentUser?.emailVerified ? (
              <span className="badge badge-success text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full border border-green-500/30">✔️ Doğrulandı</span>
            ) : (
              <Button variant="primary" size="sm" onClick={handleVerifyEmail}>Doğrula</Button>
            )}
          </div>
          
          <div className="settings-item">
            <span className="settings-label">Referans Kodun</span>
            <span className="settings-value text-accent" style={{color: 'var(--accent-primary)'}}>{userData.referralCode || '—'}</span>
          </div>

          <div className="settings-item">
            <span className="settings-label">Referans Kodu Ekle</span>
            <Button variant="ghost" size="sm" onClick={() => setShowReferralModal(true)} disabled={!!userData.referralCodeUsed}>
              {userData.referralCodeUsed || 'Ekle'}
            </Button>
          </div>


          <div className="settings-item">
            <span className="settings-label">Şifre Değiştir</span>
            <Button variant="ghost" size="sm" onClick={() => setShowPasswordModal(true)}>Değiştir</Button>
          </div>
        </GlassCard>

      </div>

      {/* ── Save Bar ── */}
      <AnimatePresence>
        {isDirty && (
          <motion.div 
            className="account-save-bar"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            style={{ }}
          >
            <div className="account-save-info">
              <span className="account-save-title">Değişiklikleri Kaydet</span>
              <span className="account-save-subtitle">Tüm düzenlemeler profile yansıyacak.</span>
            </div>
            <Button variant="primary" onClick={handleSave} style={{ minWidth: '120px', background: 'linear-gradient(90deg, #00e676, #00c853)' }}>
              Kaydet
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Modals ── */}

      {/* Character Selection Modal */}
      <Modal isOpen={showCharacterModal} onClose={() => setShowCharacterModal(false)} title="Karakterini Seç" maxWidth="500px">
        <div className="flex flex-col gap-3">
          {CHARACTER_TYPES.map((char) => (
            <div 
              key={char.id} 
              className={`panel-item cursor-pointer ${userData.characterType === char.name ? 'border-accent bg-accent/10' : ''}`}
              onClick={() => {
                setUserData({ ...userData, characterType: char.name });
                setShowCharacterModal(false);
              }}
              style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '16px', borderRadius: '12px', transition: 'all 0.2s', border: userData.characterType === char.name ? '1px solid var(--accent-primary)' : '1px solid rgba(255,255,255,0.05)' }}
            >
              <div style={{ fontSize: '32px' }}>{char.emoji}</div>
              <div className="flex flex-col">
                <span style={{ fontSize: '16px', fontWeight: 'bold', color: userData.characterType === char.name ? 'var(--accent-primary)' : '#fff' }}>{char.name}</span>
                <span style={{ fontSize: '13px', color: '#94a3b8' }}>{char.desc}</span>
              </div>
            </div>
          ))}
        </div>
      </Modal>

      {/* Badges Modal */}
      <Modal isOpen={showBadgesModal} onClose={() => setShowBadgesModal(false)} title="Kozmik Rozet Koleksiyonu" maxWidth="600px">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-secondary mb-2">Başarıların burada toplanır. Sönük olanlar kilitli, parlak olanlar ise senin!</p>
          {Object.entries(badgeConfig).map(([key, badge]) => {
            const earned = [...userData.badges, ...userData.streakBadges].includes(key);
            return (
              <div key={key} style={{
                display: 'flex', alignItems: 'center', gap: '16px', padding: '16px',
                borderRadius: '12px', background: earned ? 'rgba(0, 255, 204, 0.05)' : 'rgba(255, 255, 255, 0.02)',
                border: earned ? '1px solid rgba(0, 255, 204, 0.3)' : '1px dashed rgba(255, 255, 255, 0.1)',
                opacity: earned ? 1 : 0.6, transition: 'all 0.3s ease'
              }}>
                <div style={{
                  fontSize: '32px', width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: earned ? 'linear-gradient(135deg, rgba(0,255,204,0.2), rgba(0,255,204,0))' : 'rgba(0,0,0,0.3)',
                  borderRadius: '50%', border: earned ? '2px solid #00ffcc' : '1px solid rgba(255,255,255,0.1)',
                  boxShadow: earned ? '0 0 15px rgba(0,255,204,0.3)' : 'none', filter: earned ? 'none' : 'grayscale(100%)'
                }}>
                  {earned ? badge.icon : '🔒'}
                </div>
                <div className="flex flex-col flex-1">
                  <span style={{ fontSize: '16px', fontWeight: 'bold', color: earned ? '#00ffcc' : '#94a3b8' }}>
                    {badge.name}
                  </span>
                  <span style={{ fontSize: '13px', color: '#cbd5e1', marginTop: '4px' }}>
                    {badge.description}
                  </span>
                </div>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: earned ? '#00e676' : '#64748b', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  {earned ? 'Kazanıldı' : 'Kilitli'}
                </div>
              </div>
            );
          })}
        </div>
      </Modal>

      {/* Shareable Card Modal */}
      <Modal isOpen={showShareableCard} onClose={() => setShowShareableCard(false)} title="Paylaşılabilir Kart" maxWidth="400px">
        <UserCardShareable userData={userData} levelConfig={levelConfig} onClose={() => setShowShareableCard(false)} />
      </Modal>

      {/* Password Modal */}
      <Modal isOpen={showPasswordModal} onClose={() => setShowPasswordModal(false)} title="Şifre Değiştir">
        <div className="flex flex-col gap-4">
          <input type="password" className="input" placeholder="Mevcut şifre" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
          <input type="password" className="input" placeholder="Yeni şifre" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          <Button variant="primary" onClick={handleUpdatePassword}>Güncelle</Button>
        </div>
      </Modal>

      {/* Referral Modal */}
      <Modal isOpen={showReferralModal} onClose={() => setShowReferralModal(false)} title="Referans Kodu Ekle">
        <div className="flex flex-col gap-4">
          <input className="input" placeholder="Referans kodunu gir" value={newReferralCodeUsed} onChange={(e) => setNewReferralCodeUsed(e.target.value)} />
          <Button variant="primary" onClick={handleUpdateReferral}>Ekle</Button>
        </div>
      </Modal>

      {/* Level Up Modal */}
      <AnimatePresence>
        {showLevelUpModal && newLevel && (
          <motion.div className="level-up-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowLevelUpModal(false)}>
            <motion.div className="level-up-content" initial={{ scale: 0.8 }} animate={{ scale: 1 }} exit={{ scale: 0.8 }}>
              <div className="level-up-emoji">{getLevelInfo(newLevel).emoji}</div>
              <h2 className="level-up-title text-gradient">Seviye {newLevel}!</h2>
              <h3 className="level-up-name">{getLevelInfo(newLevel).name}</h3>
              <p className="level-up-desc">{getLevelInfo(newLevel).description}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default AccountPage;